import React, { forwardRef } from "react";

import { cn } from "../utils";

export type ButtonVariant = "solid" | "ghost" | "outline" | "link";

export type ButtonProps = {
  variant?: ButtonVariant;
  className?: string;
  disabled?: boolean;
} & (
  | (React.AnchorHTMLAttributes<HTMLAnchorElement> & {
      disabled?: never;
      loading?: never;
      type?: never;
    })
  | (React.ButtonHTMLAttributes<HTMLButtonElement> & {
      href?: never;
      target?: never;
      rel?: never;
      loading?: boolean;
    })
);
const baseStyles =
  "appearance-none select-none cursor-pointer focus:outline-none disabled:pointer-events-none text-center px-4 py-2 inline-flex gap-2 items-center justify-center";

const variantStyles = {
  solid:
    "bg-black text-white border border-white rounded-full double-border-button disabled:border-gray-700 disabled:text-gray-700",
  outline:
    "text-white border border-white rounded-full double-border-button disabled:border-gray-600 disabled:text-gray-600",
  ghost: "text-white bg-transparent disabled:opacity-40",
  link: "text-white underline disabled:opacity-40",
};

export function buttonStyles(variant: ButtonVariant) {
  return cn(baseStyles, variantStyles[variant]);
}

export const Button = forwardRef<
  HTMLButtonElement | HTMLLinkElement,
  ButtonProps
>(
  (
    {
      variant = "outline",
      className,
      loading = false,
      disabled,
      children,
      ...rest
    },
    ref,
  ) => {
    const styles = buttonStyles(variant);

    if (rest.href) {
      return (
        <a
          className={cn(styles, className)}
          {...rest}
          ref={ref as React.Ref<HTMLAnchorElement>}
        >
          {children}
        </a>
      );
    }

    return (
      <button
        className={cn(styles, className)}
        disabled={loading || disabled}
        {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        ref={ref as React.Ref<HTMLButtonElement>}
      >
        {loading ? "loading..." : children}
      </button>
    );
  },
);

Button.displayName = "Button";
